<script setup>
import CheckIcon from '@iconify-vue/ph/check';
import EyeIcon from '@iconify-vue/ph/eye';
import FileHtmlIcon from '@iconify-vue/ph/file-html';
import FilePdfIcon from '@iconify-vue/ph/file-pdf';
import LinkIcon from '@iconify-vue/ph/link';
import PaperclipIcon from '@iconify-vue/ph/paperclip';
import PlayCircleIcon from '@iconify-vue/ph/play-circle';
import WarningIcon from '@iconify-vue/ph/warning';
import XIcon from '@iconify-vue/ph/x';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import { useSecurity } from '@js/composables/security';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { FileStatus } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import { AnimatePresence, Motion } from 'motion-v';
import {
    PopoverContent,
    PopoverPortal,
    PopoverRoot,
    PopoverTrigger,
} from 'reka-ui';
import { ErrorMessage, Field } from 'vee-validate';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import vueFilePond from 'vue-filepond';

const FilePond = vueFilePond(
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
    FilePondPluginImagePreview,
);

const model = defineModel();

const emit = defineEmits(['processing']);

const {
    fieldId,
    fieldName,
    label,
    accept = '',
    maxFiles = null,
    maxFileSize = '25MB',
    legend = null,
    inlineLabel = false,
    size = 'medium',
    withoutMargin = false,
    disabled = false,
    required = false,
    compact = false,
    gallery = false,
    initialFiles = [],
    urlImportEndpoint = null,
    initialUrlImports = [],
} = defineProps({
    fieldId: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: false,
    },
    accept: {
        type: String,
        required: false,
    },
    maxFiles: {
        type: Number,
        required: false,
    },
    maxFileSize: {
        type: String,
        required: false,
    },
    legend: {
        type: String,
        required: false,
    },
    inlineLabel: {
        type: Boolean,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    withoutMargin: {
        type: Boolean,
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    required: {
        type: Boolean,
        required: false,
    },
    compact: {
        type: Boolean,
        required: false,
    },
    gallery: {
        type: Boolean,
        required: false,
    },
    initialFiles: {
        type: Array,
        required: false,
    },
    urlImportEndpoint: {
        type: String,
        required: false,
    },
    initialUrlImports: {
        type: Array,
        required: false,
    },
});

const { route } = useRoute();

const { csrfToken } = useSecurity();

const pond = ref(null);

const fileList = ref([]);

const isImportingUrls = ref(false);

const previews = new Map();

const lightboxFile = ref(null);

const lightboxOpen = ref(false);

/**
 * Display order of the grid: { kind: 'existing', id, url, filename } or { kind: 'pond', pondId }.
 */
const galleryItems = ref(
    initialFiles.map((file) => ({
        kind: 'existing',
        id: file.id,
        url: file.url,
        filename: file.filename,
    })),
);

const draggedIndex = ref(null);

const dragOverIndex = ref(null);

const acceptedFileTypes = computed(() =>
    accept
        .split(',')
        .map((type) => type.trim())
        .filter(Boolean),
);

const labelIdle = computed(() =>
    compact
        ? '<span class="filepond--label-action">+ Pièces jointes</span>'
        : 'Glissez-déposez vos fichiers ou <span class="filepond--label-action">Parcourir</span>',
);

const server = computed(() => ({
    process: route('upload.process'),
    patch: route('upload.patch') + '?patch=',
    revert: route('upload.revert'),
    headers: {
        'X-CSRF-TOKEN': csrfToken.value,
    },
}));

function fileState(status) {
    if (status === FileStatus.PROCESSING_COMPLETE) {
        return 'done';
    }

    if (
        status === FileStatus.PROCESSING_ERROR ||
        status === FileStatus.PROCESSING_REVERT_ERROR ||
        status === FileStatus.LOAD_ERROR
    ) {
        return 'error';
    }

    return 'loading';
}

const isUploading = computed(
    () =>
        fileList.value.some((file) => file.state === 'loading') ||
        isImportingUrls.value,
);

watch(isUploading, (value) => emit('processing', value));

function fileKind(file) {
    const type = file.fileType || file.file?.type || '';

    if (type.startsWith('image/')) {
        return 'image';
    }

    if (type.startsWith('video/')) {
        return 'video';
    }

    if (type === 'application/pdf') {
        return 'pdf';
    }

    if (type === 'text/html') {
        return 'html';
    }

    return 'other';
}

function previewUrlFor(file, kind) {
    if (kind === 'other' || !file.file) {
        return null;
    }

    if (!previews.has(file.id)) {
        previews.set(file.id, URL.createObjectURL(file.file));
    }

    return previews.get(file.id);
}

function sync() {
    const files = pond.value ? pond.value.getFiles() : [];
    const ids = new Set(files.map((file) => file.id));

    for (const [id, url] of previews) {
        if (!ids.has(id)) {
            URL.revokeObjectURL(url);
            previews.delete(id);
        }
    }

    fileList.value = files.map((file) => {
        const kind = fileKind(file);

        return {
            id: file.id,
            filename: file.filename,
            state: fileState(file.status),
            kind,
            preview: previewUrlFor(file, kind),
        };
    });

    if (gallery) {
        syncGallery(files);
    } else {
        model.value = files
            .filter((file) => file.serverId)
            .map((file) => file.serverId);
    }
}

function syncGallery(files) {
    const pondIds = new Set(files.map((file) => file.id));

    galleryItems.value = galleryItems.value.filter(
        (item) => item.kind === 'existing' || pondIds.has(item.pondId),
    );

    const knownPondIds = new Set(
        galleryItems.value
            .filter((item) => item.kind === 'pond')
            .map((item) => item.pondId),
    );

    for (const file of files) {
        if (!knownPondIds.has(file.id)) {
            galleryItems.value.push({ kind: 'pond', pondId: file.id });
        }
    }

    model.value = galleryItems.value
        .map((item) => {
            if (item.kind === 'existing') {
                return item.id;
            }

            return files.find((file) => file.id === item.pondId)?.serverId;
        })
        .filter((value) => value !== undefined && value !== null);
}

const galleryFiles = computed(() =>
    galleryItems.value.map((item) => {
        if (item.kind === 'existing') {
            return {
                key: `existing-${item.id}`,
                filename: item.filename,
                preview: item.url,
                kind: 'image',
                state: 'done',
            };
        }

        const file = fileList.value.find((entry) => entry.id === item.pondId);

        return {
            key: `pond-${item.pondId}`,
            pondId: item.pondId,
            filename: file?.filename,
            preview: file?.preview,
            kind: file?.kind ?? 'other',
            state: file?.state ?? 'loading',
        };
    }),
);

function removeGalleryFile(index) {
    const item = galleryItems.value[index];

    if (item === undefined) {
        return;
    }

    if (item.kind === 'pond') {
        removeFile(item.pondId);

        return;
    }

    galleryItems.value.splice(index, 1);

    sync();
}

function moveGalleryFile(fromIndex, toIndex) {
    if (fromIndex === toIndex || toIndex < 0) {
        return;
    }

    const items = [...galleryItems.value];
    const [moved] = items.splice(fromIndex, 1);

    items.splice(toIndex, 0, moved);

    galleryItems.value = items;

    sync();
}

function onGalleryDragStart(index) {
    draggedIndex.value = index;
}

function onGalleryDrop(index) {
    if (draggedIndex.value !== null) {
        moveGalleryFile(draggedIndex.value, index);
    }

    draggedIndex.value = null;
    dragOverIndex.value = null;
}

function openLightbox(file) {
    lightboxFile.value = file;
    lightboxOpen.value = true;
}

function notifyError(error) {
    const message = [error?.main, error?.sub].filter(Boolean).join(' — ');

    toastError(message, "Une erreur est survenue lors de l'ajout du fichier.");
}

function onAddFile(error, file) {
    if (error) {
        notifyError(error);

        if (file?.id) {
            removeFile(file.id);
        }

        return;
    }

    sync();
}

function onProcessFile(error, file) {
    if (error) {
        notifyError(error);

        if (file?.id) {
            removeFile(file.id);
        }

        return;
    }

    sync();
}

function browse() {
    pond.value?.browse();
}

function removeFile(id) {
    pond.value?.removeFile(id);
}

function revokePreviews() {
    for (const url of previews.values()) {
        URL.revokeObjectURL(url);
    }

    previews.clear();
}

function reset() {
    if (pond.value) {
        pond.value.removeFiles();
    }

    revokePreviews();
    fileList.value = [];
    model.value = [];
}

function addFiles(files) {
    if (pond.value) {
        pond.value.addFiles(files);
    }
}

const urlImportOpen = ref(false);

const urlImportValue = ref('');

const urlImportList = computed(() =>
    urlImportValue.value.split(/\s+/).filter(Boolean),
);

async function readImportErrorMessage(error, fallback) {
    const data = error?.response?.data;

    if (!(data instanceof Blob)) {
        return data?.message ?? fallback;
    }

    try {
        return JSON.parse(await data.text())?.message ?? fallback;
    } catch {
        return fallback;
    }
}

async function addFilesFromUrls(urls) {
    isImportingUrls.value = true;

    const files = [];

    for (const url of urls) {
        try {
            const response = await axios.post(
                urlImportEndpoint,
                { url },
                { responseType: 'blob' },
            );

            files.push(
                new File(
                    [response.data],
                    response.headers['x-media-filename'] || 'image.jpg',
                    { type: response.data.type },
                ),
            );
        } catch (error) {
            toastError(
                await readImportErrorMessage(
                    error,
                    `${url} n'a pas pu être importé.`,
                ),
            );
        }
    }

    isImportingUrls.value = false;

    if (files.length > 0) {
        addFiles(files);
    }

    return files.length;
}

async function importFromUrls() {
    if (isImportingUrls.value || urlImportList.value.length === 0) {
        return;
    }

    if ((await addFilesFromUrls(urlImportList.value)) > 0) {
        urlImportValue.value = '';
        urlImportOpen.value = false;
    }
}

onMounted(() => {
    if (gallery) {
        sync();
    }

    if (urlImportEndpoint && initialUrlImports.length > 0) {
        addFilesFromUrls(initialUrlImports);
    }
});

onBeforeUnmount(revokePreviews);

defineExpose({ reset, addFiles, isUploading });
</script>

<template>
    <div v-if="compact" class="inline-flex items-center gap-1">
        <div
            class="pointer-events-none fixed top-0 -left-[9999px] w-80"
            aria-hidden="true"
        >
            <FilePond
                ref="pond"
                :name="fieldName"
                credits=""
                :allow-multiple="true"
                :accepted-file-types="acceptedFileTypes"
                :max-files="maxFiles"
                :max-file-size="maxFileSize"
                :server="server"
                :chunk-uploads="true"
                :chunk-force="true"
                :chunk-size="1048576"
                :disabled="disabled"
                :label-idle="labelIdle"
                label-max-file-size-exceeded="Le fichier est trop volumineux"
                label-max-file-size="La taille maximale est de {filesize}"
                label-file-type-not-allowed="Type de fichier non autorisé"
                label-file-processing-error="Erreur lors de l'envoi"
                @addfile="onAddFile"
                @processfilestart="sync"
                @processfile="onProcessFile"
                @removefile="sync"
                @error="sync"
            />
        </div>

        <ActionButton
            size="small"
            type="secondary"
            :left-icon="PaperclipIcon"
            :callback="browse"
            :disabled="disabled"
        >
            Pièces jointes<template v-if="fileList.length">
                ({{ fileList.length }})</template
            >

            <span
                v-if="isUploading"
                class="size-3 animate-spin rounded-full border-2 border-current border-t-transparent"
                title="Envoi en cours…"
            />
        </ActionButton>

        <PopoverRoot v-if="fileList.length">
            <PopoverTrigger as-child>
                <ActionButton size="small" type="secondary" :icon="EyeIcon" />
            </PopoverTrigger>

            <PopoverPortal>
                <PopoverContent side="top" :side-offset="6" class="z-50">
                    <Motion
                        :initial="{ opacity: 0, scale: 0.96, y: 4 }"
                        :animate="{ opacity: 1, scale: 1, y: 0 }"
                        :transition="{ duration: 0.15 }"
                        class="max-h-60 w-72 overflow-y-auto rounded-sm border border-gray-200 bg-white p-2 drop-shadow-sm"
                    >
                        <ul class="flex flex-col gap-1">
                            <li
                                v-for="file in fileList"
                                :key="file.id"
                                class="flex items-center gap-2 rounded-sm px-1 py-1 text-sm hover:bg-gray-50"
                            >
                                <span
                                    v-if="file.state === 'loading'"
                                    class="size-3 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600"
                                />

                                <CheckIcon
                                    v-else-if="file.state === 'done'"
                                    class="size-4 shrink-0 text-green-600"
                                />

                                <WarningIcon
                                    v-else
                                    class="size-4 shrink-0 text-red-500"
                                />

                                <button
                                    v-if="file.preview"
                                    type="button"
                                    class="shrink-0 cursor-zoom-in"
                                    title="Voir en grand"
                                    @click="openLightbox(file)"
                                >
                                    <img
                                        v-if="file.kind === 'image'"
                                        :src="file.preview"
                                        :alt="file.filename"
                                        class="size-8 rounded-sm border border-gray-200 object-cover"
                                    />

                                    <span
                                        v-else-if="file.kind === 'video'"
                                        class="relative block size-8 overflow-hidden rounded-sm border border-gray-200"
                                    >
                                        <video
                                            :src="file.preview"
                                            preload="metadata"
                                            muted
                                            playsinline
                                            class="size-full bg-black object-cover"
                                        />

                                        <span
                                            class="absolute inset-0 flex items-center justify-center bg-black/30"
                                        >
                                            <PlayCircleIcon
                                                class="size-4 text-white"
                                            />
                                        </span>
                                    </span>

                                    <FilePdfIcon
                                        v-else-if="file.kind === 'pdf'"
                                        class="size-8 text-indigo-600"
                                    />

                                    <FileHtmlIcon
                                        v-else-if="file.kind === 'html'"
                                        class="size-8 text-orange-600"
                                    />
                                </button>

                                <span class="grow truncate">{{
                                    file.filename
                                }}</span>

                                <button
                                    type="button"
                                    class="shrink-0 text-gray-400 duration-150 hover:text-red-500"
                                    @click="removeFile(file.id)"
                                >
                                    <XIcon class="size-4" />
                                </button>
                            </li>
                        </ul>
                    </Motion>
                </PopoverContent>
            </PopoverPortal>
        </PopoverRoot>

        <Field :id="fieldId" :name="fieldName" type="hidden" v-model="model" />

        <ErrorMessage :name="fieldName" class="text-sm text-red-500" />

        <DialogModal v-model="lightboxOpen" size="large">
            <template #title>
                {{ lightboxFile?.filename ?? 'Aperçu' }}
            </template>

            <template #content>
                <img
                    v-if="lightboxFile?.kind === 'image'"
                    :src="lightboxFile.preview"
                    :alt="lightboxFile.filename"
                    class="mx-auto max-h-[70vh] object-contain"
                />

                <video
                    v-else-if="lightboxFile?.kind === 'video'"
                    :src="lightboxFile.preview"
                    controls
                    autoplay
                    playsinline
                    class="mx-auto max-h-[70vh] rounded-sm bg-black"
                />

                <iframe
                    v-else-if="lightboxFile?.kind === 'pdf'"
                    :src="lightboxFile.preview"
                    class="h-[70vh] w-full rounded-sm bg-white"
                />

                <iframe
                    v-else-if="lightboxFile?.kind === 'html'"
                    :src="lightboxFile.preview"
                    sandbox=""
                    class="h-[70vh] w-full rounded-sm bg-white"
                />
            </template>
        </DialogModal>
    </div>

    <!-- Default: full drop zone -->
    <div
        v-else
        class="flex"
        :class="{
            'items-center': inlineLabel,
            'flex-col': !inlineLabel,
            'mb-4': !withoutMargin,
        }"
    >
        <label
            v-if="label"
            :for="fieldId"
            class="inline-flex items-center gap-1"
            :class="{
                'mr-2': inlineLabel,
                'mb-2': !inlineLabel,
                'text-sm': size === 'small',
            }"
        >
            {{ label }}

            <sup v-if="required" class="text-red-500">*</sup>
        </label>

        <div class="w-full" :class="{ 'filepond-gallery': gallery }">
            <AnimatePresence
                v-if="gallery"
                as="ul"
                class="grid grid-cols-4 gap-2"
                :class="{ 'mb-2': galleryFiles.length }"
            >
                <Motion
                    v-for="(file, index) in galleryFiles"
                    :key="file.key"
                    as="li"
                    :initial="{ opacity: 0, scale: 0.9 }"
                    :animate="{
                        opacity: draggedIndex === index ? 0.4 : 1,
                        scale: 1,
                    }"
                    :exit="{ opacity: 0, scale: 0.9 }"
                    :transition="{ duration: 0.15 }"
                    class="group relative overflow-hidden rounded-sm border border-gray-200 duration-150"
                    :class="{
                        'ring-2 ring-indigo-400': dragOverIndex === index,
                        'cursor-grab': !disabled,
                    }"
                    :draggable="!disabled"
                    @dragstart="onGalleryDragStart(index)"
                    @dragover.prevent="dragOverIndex = index"
                    @dragleave="dragOverIndex = null"
                    @drop.prevent="onGalleryDrop(index)"
                    @dragend="
                        draggedIndex = null;
                        dragOverIndex = null;
                    "
                >
                    <img
                        v-if="file.preview && file.kind === 'image'"
                        :src="file.preview"
                        :alt="file.filename"
                        class="aspect-square w-full object-cover"
                    />

                    <span
                        v-else
                        class="flex aspect-square w-full items-center justify-center bg-gray-50"
                    >
                        <span
                            v-if="file.state === 'loading'"
                            class="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600"
                        />

                        <WarningIcon
                            v-else-if="file.state === 'error'"
                            class="size-6 text-red-500"
                        />

                        <PaperclipIcon v-else class="size-6 text-gray-400" />
                    </span>

                    <span
                        class="absolute top-1 left-1 rounded-sm bg-black/60 px-1.5 py-0.5 text-xs text-white"
                    >
                        {{ index + 1 }}
                    </span>

                    <button
                        v-if="!disabled"
                        type="button"
                        class="absolute top-1 right-1 rounded-sm bg-white/90 px-2 py-1 text-xs text-red-600 opacity-0 duration-150 group-hover:opacity-100"
                        title="Retirer"
                        @click="removeGalleryFile(index)"
                    >
                        Retirer
                    </button>
                </Motion>
            </AnimatePresence>

            <FilePond
                ref="pond"
                :name="fieldName"
                credits=""
                :allow-multiple="true"
                :accepted-file-types="acceptedFileTypes"
                :max-files="maxFiles"
                :max-file-size="maxFileSize"
                :server="server"
                :chunk-uploads="true"
                :chunk-force="true"
                :chunk-size="1048576"
                :disabled="disabled"
                :label-idle="labelIdle"
                label-max-file-size-exceeded="Le fichier est trop volumineux"
                label-max-file-size="La taille maximale est de {filesize}"
                label-file-type-not-allowed="Type de fichier non autorisé"
                label-file-loading="Chargement"
                label-file-processing="Envoi en cours"
                label-file-processing-complete="Envoi terminé"
                label-file-processing-aborted="Envoi annulé"
                label-file-processing-error="Erreur lors de l'envoi"
                label-tap-to-cancel="cliquez pour annuler"
                label-tap-to-retry="cliquez pour réessayer"
                label-tap-to-undo="cliquez pour annuler"
                @addfile="onAddFile"
                @processfilestart="sync"
                @processfile="onProcessFile"
                @removefile="sync"
                @error="sync"
            />

            <PopoverRoot v-if="urlImportEndpoint" v-model:open="urlImportOpen">
                <PopoverTrigger as-child>
                    <ActionButton
                        size="small"
                        type="secondary"
                        :left-icon="LinkIcon"
                        :disabled="disabled"
                    >
                        Ajouter depuis une adresse
                    </ActionButton>
                </PopoverTrigger>

                <PopoverPortal>
                    <PopoverContent
                        side="bottom"
                        :side-offset="6"
                        align="start"
                        class="z-50"
                    >
                        <Motion
                            :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                            :animate="{ opacity: 1, scale: 1, y: 0 }"
                            :transition="{ duration: 0.15 }"
                            class="w-96 rounded-sm border border-gray-300 bg-white p-3 drop-shadow-sm"
                        >
                            <textarea
                                v-model="urlImportValue"
                                rows="3"
                                placeholder="https://exemple.com/visuel.jpg"
                                class="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm ring-1 ring-transparent duration-150 placeholder:text-gray-400 focus:ring-gray-300 focus:outline-none"
                            ></textarea>

                            <em class="mt-1 block text-xs text-gray-400">
                                Une adresse par ligne.
                            </em>

                            <div class="mt-2 flex justify-end">
                                <ActionButton
                                    size="small"
                                    :callback="importFromUrls"
                                    :disabled="
                                        isImportingUrls || !urlImportList.length
                                    "
                                >
                                    {{
                                        isImportingUrls
                                            ? 'Import en cours...'
                                            : 'Importer'
                                    }}
                                </ActionButton>
                            </div>
                        </Motion>
                    </PopoverContent>
                </PopoverPortal>
            </PopoverRoot>

            <Field
                :id="fieldId"
                :name="fieldName"
                type="hidden"
                v-model="model"
            />

            <em v-if="legend" class="mt-1 text-xs text-gray-400">
                {{ legend }}
            </em>

            <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
        </div>
    </div>
</template>

<style scoped>
/* In gallery mode the thumbnails are rendered above: FilePond's own list would duplicate them. */
.filepond-gallery :deep(.filepond--list-scroller) {
    display: none;
}
</style>
